/**
 * Receiver resume — the §6.1 scan + §6.2 reconcile-and-continue + §6.3
 * CREATE_WAIT algorithm (docs/file-transfer-resume.md). Pure
 * `ReceiveResumeDeps`-injected core, fully unit-testable with fakes (see
 * `__tests__/receiveResume.test.ts` + `__tests__/receiveResume.multifile.test.ts`);
 * `receiveResumeDeps.ext.ts` supplies the real OPFS-worker / HTTP / Web-Locks /
 * crypto wiring and the offscreen host's activeReceives/idle/broadcast
 * bookkeeping.
 *
 * The receiver loop runs in the OFFSCREEN document (§3.1.2 — "receiver loops
 * run in the offscreen document"), so this module lives beside
 * `receiveHost.ts` (the fresh-accept path) rather than under `sidepanel/`.
 * It drives the SAME shared `runReceiveLoop` primitive `receiveHost.ts` uses
 * (the "one code path" requirement) — starting it at `startPartIndex` and
 * bypassing `receiveHost.ts`'s fresh-accept prologue (output.open + accept
 * ctrl), because a resume opens EXISTING files, truncates each to its own
 * last-verified boundary, and rebuilds the digest before continuing.
 *
 * MULTI-FILE RESUME (multi-file transfer design §6,
 * docs/superpowers/specs/2026-07-26-multi-file-transfer-design.md — "resume is
 * the hard part"): `record.chunksVerified` is a single GLOBAL cross-file chunk
 * cursor (`receiveLoop.ts`'s `onPartWritten` cursor — cumulative chunkCount of
 * every earlier file + the current file's own local high-water chunk index +
 * 1). Because `runReceiveLoop` verifies a file's whole-file SHA-256 the moment
 * it writes that file's OWN last chunk, STRICTLY BEFORE `onPartWritten`
 * persists the advanced cursor, ANY file whose chunks are entirely `<=` the
 * persisted `chunksVerified` was already verified pre-crash — its bytes are
 * durable, and it can be skipped outright on resume (no truncate, no
 * readRange rebuild, no re-download, no re-verify). Only the FIRST
 * not-fully-covered file (if any) needs its digest reconstructed from its own
 * retained OPFS prefix; every file after that hasn't been started yet. A
 * single-file transfer is the degenerate one-file case of this same
 * derivation (byte-identical to the pre-multi-file formulas).
 *
 * §2.4 load-time validation is applied HERE, not in `receiverState.ts` (a
 * plain CRUD layer) — this module holds the OPFS deps needed to delete a
 * dropped record's output artifacts.
 *
 * SECURITY: never log file name/content/manifest fields — security.md.
 */

import type { FileCtrl, TransferManifest } from "@reflecto/core";
import { CHUNKS_PER_PART, ChunkAuthError, decodeBase64, validateManifest } from "@reflecto/core";
import { isDefinitive404, isNetworkError } from "./errors";
import { type FinalizedFile, rewrapFinalizedFile } from "./fileSeam";
import { ftLog } from "./ftLog";
import {
  type FtTerminalOutcome,
  isKnownTransferStatus,
  isTerminalReceiverStatus,
} from "./lifecycle";
import type { FtTransport } from "./p2pTypes";
import { globalChunkOffsets, partPlan } from "./partPlan";
import { createThrottledProgressSender } from "./progressThrottle";
import {
  HashMismatchError,
  ReceiveAbortedError,
  type ReceiveHandoffDisposition,
  type ReceiveLoopCrypto,
  type ReceiveLoopHash,
  type ReceiveLoopOutput,
  runReceiveLoop,
} from "./receiveLoop";
import {
  FT_ACCEPT_RESEND_MS,
  FT_CREATE_WAIT_MS,
  FT_POLL_ACTIVE_MS,
  FT_POLL_IDLE_AFTER_MS,
  FT_POLL_IDLE_MS,
  FT_RECEIVE_LIVENESS_MS,
  type FtReceiverStateRecord,
} from "./state";
import { isQuotaExceededError } from "./workerError";

/**
 * Per-part retry budget for download NETWORK/presigned failures (§6.2 step 7
 * / §11 `PART_RETRY_BACKOFF_MS`). Redeclared locally (not imported from the
 * sender's `sidepanel/filetransfer/uploadLoop.ts`) to keep the offscreen
 * bundle free of the panel-only sender pipeline — the same
 * redeclare-locally-per-surface pattern the CLI uses for §11 constants
 * (§3.3). Values are the normative §11 table (kept in sync with
 * `uploadLoop.ts`'s copy).
 */
const PART_RETRY_BACKOFF_MS = [1000, 4000, 16000] as const;

/** `GET /v1/transfers/:id`'s reconciliation snapshot (narrowed to what resume needs). */
export interface ResumeTransferStatus {
  status: string;
  partCount: number;
  partsUploaded: number[];
  expiresAt: number;
}

export interface ReceiveResumeApi {
  getTransfer(transferId: string): Promise<ResumeTransferStatus>;
  getDownloadUrl(transferId: string, partIndex: number): Promise<{ url: string }>;
  fetchPartStream(url: string): Promise<ReadableStream<Uint8Array>>;
  /** Receiver-owned DELETE on its own terminal failures (§6.2 step 8, §6.5) — best-effort. */
  deleteTransfer(transferId: string): Promise<void>;
}

/** OPFS output seam for ONE file of a transfer (worker-client-backed). Satisfies `ReceiveLoopOutput` (writeAt/flush) so it feeds `runReceiveLoop` directly. */
export interface ReceiveResumeOutput extends ReceiveLoopOutput {
  /** Async-API probe (does NOT open the sync handle) — current byte size, or `null` if the file doesn't exist (§6.2 step 1). */
  probeSize(): Promise<number | null>;
  /** Open the OPFS sync access handle on the (possibly existing) file — create-if-missing, never truncates. */
  open(): Promise<void>;
  /** §6.2 step 3 — truncate to a part boundary. */
  truncate(size: number): Promise<void>;
  /** §6.2 step 5 — read back `[start, end)` for the digest rebuild. */
  readRange(start: number, end: number): Promise<Uint8Array>;
  /** Release the sync handle and hand back the finished file for the downloads handoff. A browser/OPFS adapter returns a real `File`; a path-based adapter (the CLI) returns a `FinalizedFileCarrier` — see [FinalizedFile]. */
  finalize(): Promise<FinalizedFile>;
  /** Delete the OPFS copy — terminal cleanup (P-R4) or post-handoff cleanup. */
  deleteCopy(): Promise<void>;
  /** Terminate the underlying worker — called once the transfer settles. */
  dispose(): void;
}

export interface ReceiveResumeCrypto extends ReceiveLoopCrypto {
  deriveTransferKey(
    sharedSecret: Uint8Array,
    transferId: string,
    senderPub: Uint8Array,
    recipientPub: Uint8Array
  ): Promise<Uint8Array>;
}

export interface ReceiveResumeHash extends ReceiveLoopHash {
  init(): unknown | Promise<unknown>;
  final(state: unknown): Uint8Array;
}

/** The finished OPFS output handed off to the OS. Returns whether the handoff is CONFIRMED complete (`"durable"`) or merely started (`"detached"`) — the core deletes the staged copy only for `"durable"`, because deleting under an in-flight read invalidates it. */
export interface ReceiveResumeDownloads {
  download(file: FinalizedFile, suggestedName: string): Promise<ReceiveHandoffDisposition>;
}

/**
 * The receiver's terminal outcome — an alias of the canonical
 * [FtTerminalOutcome] (`lifecycle.ts`), kept under this name because it is
 * the published `onTerminal` callback vocabulary the extension/web adapters
 * already import.
 */
export type ResumeTerminalOutcome = FtTerminalOutcome;

/** Every side effect the receiver resume algorithm needs, injected for testability (see module doc). */
export interface ReceiveResumeDeps {
  /** Raw (unvalidated) records — §2.4 validation runs inside this module. */
  listReceiverStates(): Promise<FtReceiverStateRecord[]>;
  saveReceiverState(record: FtReceiverStateRecord): Promise<void>;
  removeReceiverState(transferId: string): Promise<void>;
  /** Single-writer Web Lock (§3.1.2, `{ ifAvailable: true }`). `false` → another context drives it → skip silently. */
  acquireLock(transferId: string): Promise<boolean>;
  releaseLock(transferId: string): Promise<void>;
  api: ReceiveResumeApi;
  /**
   * Build the OPFS output seam for `transferId`'s file `fileIndex` (spawns a
   * worker client per file — multi-file transfer design §6,
   * docs/superpowers/specs/2026-07-26-multi-file-transfer-design.md). A
   * single-file transfer always calls this with `fileIndex 0`, so a
   * single-arg real implementation (every adapter today) stays valid —
   * TypeScript allows a function with fewer parameters wherever more are
   * declared.
   */
  openOutput(transferId: string, fileIndex: number): ReceiveResumeOutput;
  crypto: ReceiveResumeCrypto;
  hash: ReceiveResumeHash;
  downloads: ReceiveResumeDownloads;
  sendCtrl(peerDeviceId: string, ctrl: FileCtrl): Promise<void>;
  /** Raw X25519 shared secret for (own secret key, sender's pub) — re-derived, never persisted (§2.5). */
  deriveSharedSecret(senderPub: Uint8Array): Promise<Uint8Array>;
  /** OUR OWN (recipient) public key — crypto-spec.md §3 fixed role order. */
  ownPublicKey: Uint8Array;
  now?(): number;
  sleep?(ms: number): Promise<void>;
  /** Scan-level global abort (unpair) — checked between records; true stops the scan (§3.1.3). */
  isAborted?(): boolean;
  /** Offscreen-host bookkeeping: register in activeReceives + idle.transferStarted. `false` → already running here → skip. */
  beginTransfer?(transferId: string): boolean;
  /** Offscreen-host bookkeeping: remove from activeReceives + idle.transferEnded. */
  endTransfer?(transferId: string): void;
  /** Per-loop cooperative abort (cancel/error ctrl or unpair) — fed to `runReceiveLoop`'s `isAborted` (between-parts granularity). */
  isLoopAborted?(transferId: string): boolean;
  /** Broadcast progress (FILE_RECEIVE_PROGRESS). */
  onProgress?(transferId: string, partIndex: number, partCount: number): void;
  /** Broadcast terminal outcome (FILE_RECEIVE_DONE). */
  onTerminal?(
    transferId: string,
    outcome: ResumeTerminalOutcome,
    errorCode?: string,
    /** Which path carried the bytes — see [FtTransport]. Not reconstructable after the fact. */
    transport?: FtTransport
  ): void;
}

/** DEFINITIVE_404 surfaced from inside the download loop's `fetchPart` — routed to §6.3/§7.2 by the loop's catch. */
class ResumeExpiredSignal extends Error {
  constructor() {
    super("transfer definitively gone (404)");
    this.name = "ResumeExpiredSignal";
  }
}

/** Retry budget exhausted (network/presigned) — leave the record "transferring" and stop; the next scan retries (§7.3). */
class ResumeStopSignal extends Error {
  constructor() {
    super("part retry budget exhausted; stopping (record left transferring)");
    this.name = "ResumeStopSignal";
  }
}

/** [0, 1, …, count-1]. Empty when count <= 0. */
function rangeExclusive(count: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < count; i++) out.push(i);
  return out;
}

/**
 * §2.4 drop-on-load validation. Returns the parsed manifest for an active
 * record kept valid, an empty placeholder for a terminal tombstone (exempt
 * from manifest validation), or `null` if the record must be dropped.
 */
function validateRecord(record: FtReceiverStateRecord): TransferManifest | null {
  if (record.v !== 1) return null;
  if (!isKnownTransferStatus(record.status)) return null;
  if (isTerminalReceiverStatus(record.status)) return {} as TransferManifest; // tombstones exempt (§2.4.3)
  try {
    const manifest = JSON.parse(record.manifestJson) as TransferManifest;
    if (validateManifest(manifest) !== null) return null;
    return manifest;
  } catch {
    return null;
  }
}

function nowOf(deps: ReceiveResumeDeps): number {
  return deps.now ? deps.now() : Date.now();
}

function sleepOf(deps: ReceiveResumeDeps, ms: number): Promise<void> {
  return deps.sleep ? deps.sleep(ms) : new Promise((r) => setTimeout(r, ms));
}

/** Best-effort delete of EVERY file's OPFS copy — terminal cleanup (P-R4) or post-handoff cleanup, mirrors `receiveHost.ts`'s `deleteAllOutputs`. */
async function deleteAllOutputs(outputs: readonly ReceiveResumeOutput[]): Promise<void> {
  for (const output of outputs) {
    await output.deleteCopy().catch(() => {});
  }
}

async function persistTerminal(
  record: FtReceiverStateRecord,
  status: FtTerminalOutcome,
  errorCode: string | undefined,
  deps: ReceiveResumeDeps
): Promise<void> {
  record.status = status;
  record.errorCode = errorCode;
  record.updatedAt = nowOf(deps);
  await deps.saveReceiverState(record); // P-R4
}

/** §7.2 EXPIRED terminal path — persist "expired", delete every file's partial output, best-effort error{EXPIRED} ctrl. */
async function expire(
  record: FtReceiverStateRecord,
  outputs: readonly ReceiveResumeOutput[],
  deps: ReceiveResumeDeps,
  /** Reported at the terminal — see [FtTransport]. A failed post-demote resume must still say `relay_after_demote`. */
  transport: FtTransport = "relay"
): Promise<void> {
  await persistTerminal(record, "expired", "EXPIRED", deps);
  await deleteAllOutputs(outputs);
  await deps
    .sendCtrl(record.peerDeviceId, {
      kind: "error",
      transferId: record.transferId,
      code: "EXPIRED",
      message: "",
    })
    .catch(() => {});
  deps.onTerminal?.(record.transferId, "expired", "EXPIRED", transport);
}

/** §6.2 step 2 partCount mismatch — terminal "failed"/"INTERNAL", delete every file's partial output, best-effort error ctrl + DELETE. */
async function failInternal(
  record: FtReceiverStateRecord,
  outputs: readonly ReceiveResumeOutput[],
  deps: ReceiveResumeDeps,
  /** Reported at the terminal — see [FtTransport]. A failed post-demote resume must still say `relay_after_demote`. */
  transport: FtTransport = "relay"
): Promise<void> {
  await deps
    .sendCtrl(record.peerDeviceId, {
      kind: "error",
      transferId: record.transferId,
      code: "INTERNAL",
      message: "",
    })
    .catch(() => {});
  await deps.api.deleteTransfer(record.transferId).catch(() => {});
  await persistTerminal(record, "failed", "INTERNAL", deps);
  await deleteAllOutputs(outputs);
  deps.onTerminal?.(record.transferId, "failed", "INTERNAL", transport);
}

/** §6.2 step 8 mismatch OR a mid-loop terminal failure (CHUNK_AUTH_FAIL / STORAGE_FULL) — error ctrl + best-effort DELETE + delete every file's output + terminal "failed". */
async function failTerminal(
  record: FtReceiverStateRecord,
  outputs: readonly ReceiveResumeOutput[],
  code: "HASH_MISMATCH" | "CHUNK_AUTH_FAIL" | "STORAGE_FULL" | "INTERNAL",
  deps: ReceiveResumeDeps,
  /** Reported at the terminal — see [FtTransport]. A failed post-demote resume must still say `relay_after_demote`. */
  transport: FtTransport = "relay"
): Promise<void> {
  await deps
    .sendCtrl(record.peerDeviceId, {
      kind: "error",
      transferId: record.transferId,
      code,
      message: "",
    })
    .catch(() => {});
  await deps.api.deleteTransfer(record.transferId).catch(() => {});
  await deleteAllOutputs(outputs);
  await persistTerminal(record, "failed", code, deps);
  deps.onTerminal?.(record.transferId, "failed", code, transport);
}

/**
 * §6.3 CREATE_WAIT — distinguishing "not created yet" from "expired" on a
 * DEFINITIVE_404 for an active receiver record. Entered from step 2's GET
 * 404 (may CREATE_WAIT) and, defensively, from a mid-loop 404 (always
 * EXPIRED there because `expiresAt > 0`).
 */
async function createWaitOrExpire(
  record: FtReceiverStateRecord,
  manifest: TransferManifest,
  outputs: readonly ReceiveResumeOutput[],
  outputLengths: readonly number[],
  deps: ReceiveResumeDeps,
  transport: FtTransport = "relay"
): Promise<void> {
  const acceptedAt = record.acceptedAt; // anchor: never re-based on restart
  let lastAcceptSentAt = Number.NEGATIVE_INFINITY;

  for (;;) {
    if (deps.isLoopAborted?.(record.transferId)) return;
    const t = nowOf(deps);
    const inCreateWait = record.expiresAt === 0 && t < acceptedAt + FT_CREATE_WAIT_MS;
    if (!inCreateWait) {
      await expire(record, outputs, deps, transport);
      return;
    }
    // Re-send accept (no resumeFromChunk — expiresAt===0 ⇒ chunksVerified===0)
    // once on entry, then at most once every FT_ACCEPT_RESEND_MS.
    if (
      lastAcceptSentAt === Number.NEGATIVE_INFINITY ||
      t - lastAcceptSentAt >= FT_ACCEPT_RESEND_MS
    ) {
      await deps
        .sendCtrl(record.peerDeviceId, { kind: "accept", transferId: record.transferId })
        .catch(() => {});
      lastAcceptSentAt = t;
    }
    await sleepOf(deps, FT_POLL_ACTIVE_MS);
    let status: ResumeTransferStatus;
    try {
      status = await deps.api.getTransfer(record.transferId);
    } catch (err) {
      if (isDefinitive404(err)) continue; // still not created — loop re-checks the window
      return; // NETWORK/other — never expire locally; leave "transferring"; stop
    }
    // GET 200 — the transfer now exists; continue the normal flow from step 2's success.
    await continueFromGet(record, manifest, outputs, status, outputLengths, deps, transport);
    return;
  }
}

/**
 * §6.2 steps 2-tail..8: persist `expiresAt`, sanity-check `partCount`, derive
 * each file's resume bucket (multi-file transfer design §6 — module doc's
 * "MULTI-FILE RESUME"), rebuild the one boundary file's digest, re-derive the
 * key, run the availability-gated download loop, and finish (per-file verify
 * → ctrl → downloads handoff, or mismatch → fail).
 *
 * Per-file completion derivation (design doc §6's "resume is the hard part"):
 * walking `manifest.files` in order against the persisted GLOBAL
 * `record.chunksVerified` cursor —
 *   - a file entirely COVERED by the cursor (`fileChunkOffsets[i] +
 *     chunkCount[i] <= chunksVerified`) was already verified pre-crash (see
 *     module doc) — SKIPPED outright: no truncate, no readRange, no
 *     re-download, no re-verify. Its hash state is never touched.
 *   - the FIRST not-fully-covered file is the resume boundary: its wanted
 *     local prefix (`chunksVerified - fileChunkOffsets[i]`, clamped into
 *     `[0, chunkCount[i]]`) is defensively re-clamped against what's
 *     ACTUALLY on disk for that file (`floor(outputLengths[i] / chunkSize)`,
 *     mirroring the pre-multi-file single-file formula) and part-aligned
 *     (`floor(.. / CHUNKS_PER_PART) * CHUNKS_PER_PART` — a file's own local
 *     parts are `CHUNKS_PER_PART`-sized except its last). A non-zero prefix
 *     is truncated to that boundary and its digest rebuilt from the retained
 *     bytes, in bounded slices; a zero prefix means the file hasn't started —
 *     truncated to 0 defensively, fresh hash state.
 *   - every file AFTER the boundary hasn't been touched yet (transfers write
 *     files strictly in manifest order) — truncated to 0 defensively, fresh
 *     hash state.
 *
 * The resulting GLOBAL resume chunk and its corresponding GLOBAL part index
 * (`startPartIndex`) are derived from the SAME per-file/per-part offsets
 * `partPlan` itself uses (cumulative chunk counts / cumulative
 * `ceil(chunkCount / CHUNKS_PER_PART)` part counts) rather than a single
 * `ceil(globalChunk / CHUNKS_PER_PART)` division — that naive division only
 * coincides with the correct global part index when at most one earlier file
 * has a non-`CHUNKS_PER_PART`-multiple `chunkCount`; with two or more (the
 * common case for real files), it under-counts and can point back INTO an
 * already-fully-verified file's own last (short) part, corrupting that
 * file's resumed digest. Deriving from each file's own cumulative part
 * offset avoids that class of bug entirely and is exact for every case,
 * including the single-file degenerate one.
 */
async function continueFromGet(
  record: FtReceiverStateRecord,
  manifest: TransferManifest,
  outputs: readonly ReceiveResumeOutput[],
  status: ResumeTransferStatus,
  outputLengths: readonly number[],
  deps: ReceiveResumeDeps,
  /** Reported at the terminal — see [FtTransport]. */
  transport: FtTransport = "relay"
): Promise<void> {
  const transferId = record.transferId;
  const files = manifest.files;
  const chunkSize = manifest.chunkSize;
  const plan = partPlan(files.map((f) => ({ fileId: f.fileId, chunkCount: f.chunkCount })));
  const partCount = plan.length;
  const resumeFiles = files.map((f) => ({
    fileId: f.fileId,
    chunkCount: f.chunkCount,
    size: f.size,
    sha256: f.sha256,
  }));

  // Per-file GLOBAL chunk offset — the SHARED helper `receiveLoop.ts`'s
  // `onPartWritten` cursor and the AEAD nonce counter (crypto-spec.md §6a) both
  // use, so the resume cursor math can never drift from the nonce math.
  const fileChunkOffsets = globalChunkOffsets(files.map((f) => f.chunkCount));
  // Per-file GLOBAL part offset (cumulative) — the same math `partPlan` uses.
  // Deliberately NOT derived from `globalChunkOffsets` above: that helper sums
  // CHUNK counts, this sums per-file PART counts (ceil(chunkCount / CHUNKS_PER_PART)),
  // and a file's part count is not chunkOffset arithmetic — parts never straddle
  // files, so each file's remainder chunks round up to a whole part.
  const filePartOffsets: number[] = [];
  {
    let cumulativeParts = 0;
    for (const f of files) {
      filePartOffsets.push(cumulativeParts);
      cumulativeParts += Math.ceil(f.chunkCount / CHUNKS_PER_PART);
    }
  }
  const totalChunks = fileChunkOffsets[files.length - 1] + files[files.length - 1].chunkCount;

  // Step 2 tail — §9.2 "first successful GET" persist + partCount sanity.
  record.expiresAt = status.expiresAt;
  record.updatedAt = nowOf(deps);
  await deps.saveReceiverState(record);
  if (status.partCount !== record.partCount) {
    await failInternal(record, outputs, deps, transport);
    return;
  }

  // Steps 3+5 — see this function's doc. `startPartIndex` defaults to
  // `partCount` (every file fully covered — the all-downloaded case: the
  // download loop below will run zero iterations and no file is re-verified,
  // per the module doc's durability invariant).
  async function computeResumeState(): Promise<{
    resumeChunk: number;
    startPartIndex: number;
    hashStates: unknown[];
  }> {
    const hashStates: unknown[] = new Array(files.length);
    let resumeChunk = totalChunks;
    let startPartIndex = partCount;
    let boundaryHandled = false;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const offset = fileChunkOffsets[i];
      if (!boundaryHandled) {
        // Skip a file only when the cursor says it's complete AND its on-disk bytes still
        // match its manifest size. The length cross-check restores the pre-multi-file
        // single-file guard (`chunksVerified===chunkCount && outputLength===file.size`): a
        // file whose OPFS bytes shrank out-of-band (partial eviction / torn write — short of
        // fully gone, which `probeSize()===null` already resets the whole record for) must NOT
        // be trusted-and-skipped, or a truncated file would be handed straight to
        // finalize()/download. A length mismatch drops through to make THIS file the boundary
        // (truncate + rebuild its retained prefix, re-download the rest).
        if (offset + file.chunkCount <= record.chunksVerified && outputLengths[i] === file.size) {
          continue; // fully verified pre-crash AND intact on disk — hashStates[i] stays unused
        }
        boundaryHandled = true;
        const wantedLocal = Math.max(0, Math.min(file.chunkCount, record.chunksVerified - offset));
        const onDiskLocal = Math.floor(outputLengths[i] / chunkSize);
        const localPrefixChunks =
          Math.floor(Math.min(wantedLocal, onDiskLocal) / CHUNKS_PER_PART) * CHUNKS_PER_PART;
        resumeChunk = offset + localPrefixChunks;
        startPartIndex = filePartOffsets[i] + localPrefixChunks / CHUNKS_PER_PART;
        if (localPrefixChunks > 0) {
          await outputs[i].truncate(localPrefixChunks * chunkSize);
          const hashState = await deps.hash.init();
          const prefixEnd = localPrefixChunks * chunkSize;
          const sliceBytes = CHUNKS_PER_PART * chunkSize;
          for (let start = 0; start < prefixEnd; start += sliceBytes) {
            const end = Math.min(start + sliceBytes, prefixEnd);
            const slice = await outputs[i].readRange(start, end);
            deps.hash.update(hashState, slice);
          }
          hashStates[i] = hashState;
        } else {
          await outputs[i].truncate(0).catch(() => {});
          hashStates[i] = await deps.hash.init();
        }
        continue;
      }
      // Strictly after the boundary file — never started (transfers write
      // files in manifest order).
      await outputs[i].truncate(0).catch(() => {});
      hashStates[i] = await deps.hash.init();
    }
    return { resumeChunk, startPartIndex, hashStates };
  }

  let resumeState: { resumeChunk: number; startPartIndex: number; hashStates: unknown[] };
  let restarted = false;
  try {
    resumeState = await computeResumeState();
  } catch {
    // Read failure while rebuilding a digest → restart the WHOLE transfer
    // from 0 (mirrors the pre-multi-file single-file fallback, generalized
    // to every file) — never leave a half-rebuilt state.
    for (const output of outputs) await output.truncate(0).catch(() => {});
    record.chunksVerified = 0;
    record.partsDownloaded = [];
    record.updatedAt = nowOf(deps);
    await deps.saveReceiverState(record);
    restarted = true;
    const hashStates: unknown[] = [];
    for (let i = 0; i < files.length; i++) hashStates.push(await deps.hash.init());
    resumeState = { resumeChunk: 0, startPartIndex: 0, hashStates };
  }
  const { resumeChunk, startPartIndex, hashStates } = resumeState;
  const hashStateFor = (fileIndex: number): unknown => hashStates[fileIndex];

  if (!restarted && resumeChunk !== totalChunks) {
    record.chunksVerified = resumeChunk;
    record.partsDownloaded = rangeExclusive(startPartIndex);
    record.updatedAt = nowOf(deps);
    await deps.saveReceiverState(record);
  }

  // Step 6 — re-derive transferKey (peerPub here is the SENDER's pub).
  const senderPub = decodeBase64(record.peerPub);
  const sharedSecret = await deps.deriveSharedSecret(senderPub);
  const transferKey = await deps.crypto.deriveTransferKey(
    sharedSecret,
    transferId,
    senderPub,
    deps.ownPublicKey
  );
  const noncePrefix = decodeBase64(manifest.noncePrefix);

  // Step 7 — availability-gated download loop from `startPartIndex` (a
  // no-op loop — zero iterations — for the all-downloaded case).
  let latestParts = new Set(status.partsUploaded);
  let lastProgressAt = nowOf(deps);
  let prevPartsSize = latestParts.size;

  /** §6.2 step 7 availability gate — poll GET until `partIndex` appears (active→idle cadence); DEFINITIVE_404 → §6.3/§7.2, NETWORK budget → stop. */
  async function waitForPart(partIndex: number): Promise<void> {
    if (latestParts.has(partIndex)) return;
    let networkFailures = 0;
    for (;;) {
      if (deps.isLoopAborted?.(transferId)) throw new ReceiveAbortedError();
      // #821 post-accept liveness: the server answers 200 with an unchanging `partsUploaded` for the
      // full 12h FT_TTL_SECONDS when a sender dies after creating the transfer, so `lastProgressAt`
      // (already maintained below for the poll cadence) is the only signal that the peer is gone.
      // Stop rather than fail: the record stays "transferring", so a Doze-suspended uploader is
      // retried by the next scan instead of being killed. See FT_RECEIVE_LIVENESS_MS.
      const sinceProgress = nowOf(deps) - lastProgressAt;
      if (sinceProgress >= FT_RECEIVE_LIVENESS_MS) {
        // Identity for the give-up (#818 Part 4's rule): a receive that simply stops is
        // indistinguishable, in the logs, from one still waiting — and "it just went quiet" is
        // exactly the shape that cost a full debugging session. Non-sensitive scalars only.
        ftLog("terminal", {
          transferId,
          role: "receiver",
          outcome: "stalled",
          reason: "peer_stalled",
          elapsedMs: sinceProgress,
        });
        throw new ResumeStopSignal();
      }
      const idle = nowOf(deps) - lastProgressAt >= FT_POLL_IDLE_AFTER_MS;
      await sleepOf(deps, idle ? FT_POLL_IDLE_MS : FT_POLL_ACTIVE_MS);
      let poll: ResumeTransferStatus;
      try {
        poll = await deps.api.getTransfer(transferId);
      } catch (err) {
        if (isDefinitive404(err)) throw new ResumeExpiredSignal();
        networkFailures++;
        if (networkFailures > PART_RETRY_BACKOFF_MS.length) throw new ResumeStopSignal();
        continue;
      }
      networkFailures = 0;
      latestParts = new Set(poll.partsUploaded);
      if (latestParts.size > prevPartsSize) {
        lastProgressAt = nowOf(deps);
        prevPartsSize = latestParts.size;
      }
      if (latestParts.has(partIndex)) return;
    }
  }

  /** Fetch one part's ciphertext stream with the PART_RETRY_BACKOFF_MS budget (fresh URL each attempt). DEFINITIVE_404 → §7.2; exhausted → stop. */
  async function fetchPartStream(partIndex: number): Promise<ReadableStream<Uint8Array>> {
    const maxAttempts = PART_RETRY_BACKOFF_MS.length + 1;
    let attempt = 0;
    for (;;) {
      if (attempt > 0) await sleepOf(deps, PART_RETRY_BACKOFF_MS[attempt - 1]);
      try {
        const { url } = await deps.api.getDownloadUrl(transferId, partIndex);
        return await deps.api.fetchPartStream(url);
      } catch (err) {
        if (isDefinitive404(err)) throw new ResumeExpiredSignal();
        attempt++;
        if (attempt >= maxAttempts) throw new ResumeStopSignal();
      }
    }
  }

  // The receiver's throttled WIRE progress ctrl (§8.2/§9.2) — best-effort,
  // fire-and-forget; local (own-side) `deps.onProgress` above stays
  // untouched and unthrottled. KNOWN GAP (multi-file, mirrors
  // `receiveHost.ts`'s identical gap): the wire `progress` ctrl is
  // fileId-scoped — always tagged with file 0's id, even while a LATER
  // file is what's actually transferring. Unreachable in production today
  // (the offer-acceptance gate still rejects N-file manifests); per-file
  // wire progress multiplexing for real N-file transfers is later work.
  const sendProgressCtrl = createThrottledProgressSender((chunksDone) => {
    void deps
      .sendCtrl(record.peerDeviceId, {
        kind: "progress",
        transferId,
        fileId: files[0].fileId,
        chunksDone,
      })
      .catch(() => {});
  });

  try {
    await runReceiveLoop({
      fetchPart: async (partIndex) => {
        await waitForPart(partIndex);
        return { stream: await fetchPartStream(partIndex) };
      },
      crypto: deps.crypto,
      hash: deps.hash,
      plan,
      files: resumeFiles,
      outputFor: (fileIndex) => outputs[fileIndex],
      hashStateFor,
      transferKey,
      noncePrefix,
      transferId,
      chunkSize,
      partCount: record.partCount,
      startPartIndex,
      onPartWritten: async (partIndex, chunksVerified) => {
        record.chunksVerified = chunksVerified;
        // `partIndex + 1` (parts 0..partIndex are done) — NOT a
        // `ceil(chunksVerified / CHUNKS_PER_PART)` derivation, which can
        // under-count once an earlier file's own last part was short (see
        // this function's doc).
        record.partsDownloaded = rangeExclusive(partIndex + 1);
        record.updatedAt = nowOf(deps);
        await deps.saveReceiverState(record); // P-R2
        deps.onProgress?.(transferId, partIndex, record.partCount);
      },
      onProgressCtrl: sendProgressCtrl,
      isAborted: () => deps.isLoopAborted?.(transferId) ?? false,
    });
  } catch (err) {
    if (err instanceof ReceiveAbortedError) {
      // Cancel/unpair — no ctrl re-send (canceller already knows, §8.3), no DELETE; delete every output; terminal cancelled.
      await deleteAllOutputs(outputs);
      record.status = "cancelled";
      record.updatedAt = nowOf(deps);
      await deps.saveReceiverState(record); // P-R4
      deps.onTerminal?.(transferId, "cancelled", undefined, transport);
      return;
    }
    if (err instanceof ResumeExpiredSignal || isDefinitive404(err)) {
      // expiresAt > 0 here (a successful GET already happened) → EXPIRED.
      await expire(record, outputs, deps, transport);
      return;
    }
    if (err instanceof ResumeStopSignal || isNetworkError(err)) {
      return; // leave "transferring"; next scan retries (§7.3)
    }
    if (err instanceof HashMismatchError) {
      // The loop's own per-file verification (receiveLoop.ts) caught a mismatch —
      // same terminal shape as the old post-loop "Step 8" digest compare.
      await failTerminal(record, outputs, "HASH_MISMATCH", deps, transport);
      return;
    }
    if (err instanceof ChunkAuthError) {
      await failTerminal(record, outputs, "CHUNK_AUTH_FAIL", deps, transport);
      return;
    }
    // Sink write failure (quota) or any other local fault (§9.2 sink-write-fail row).
    await failTerminal(
      record,
      outputs,
      isQuotaExceededError(err) ? "STORAGE_FULL" : "INTERNAL",
      deps,
      transport
    );
    return;
  }
  await deps
    .sendCtrl(record.peerDeviceId, { kind: "complete", transferId, verified: true })
    .catch(() => {});
  await persistTerminal(record, "complete", undefined, deps); // P-R4
  deps.onTerminal?.(transferId, "complete", undefined, transport);
  // Per-file finalize -> downloads handoff -> delete the OPFS copy ONLY after
  // that file's handoff succeeds (TASK-17 / P-R4), ONE FILE AT A TIME, in
  // manifest order — mirrors `receiveHost.ts`'s identical tail.
  // Re-wrapped with the manifest's true name+MIME — OPFS files carry `type: ""`,
  // which Chrome's safe-filename pass rewrites to a `.txt` name (see
  // receiveHost.ts's identical tail for the full story).
  for (let i = 0; i < files.length; i++) {
    const fileEntry = files[i];
    const finished = await outputs[i].finalize();
    // `rewrapFinalizedFile` (fileSeam.ts) owns the Blob-only re-wrap rule for BOTH receive tails
    // — see its doc for the #824 history (a path-carrying CLI carrier must pass through
    // untouched; an OPFS File must gain the manifest's name+mime).
    const typedFinished = rewrapFinalizedFile(finished, fileEntry);
    const disposition = await deps.downloads.download(typedFinished, fileEntry.name);
    // Same rule as receiveHost's tail: a "detached" handoff is still reading these
    // bytes, and removing the OPFS entry invalidates the File mid-read. Leave it
    // for gcOrphanTransfers (the record is terminal, so it sweeps as an orphan).
    if (disposition === "durable") await outputs[i].deleteCopy();
  }
}

/**
 * §6.2 reconcile-and-continue — one full pass over a single `"transferring"`
 * receiver record. Owns EVERY file's OPFS output lifetime (spawn → dispose).
 *
 * Exported (docs/file-transfer-p2p.md §16 pt.4 — "the §6.2 reconcile core")
 * so a P2P demotion can call it DIRECTLY, inside the Web Lock the demoting
 * context already holds, instead of re-entering via `resumeReceiverTransfer`'s
 * own `{ ifAvailable: true }` lock acquisition — which would see the lock as
 * already held by this very context and silently skip. Callers reusing this
 * export MUST already hold `transferId`'s Web Lock (and its `activeReceives`
 * bookkeeping, if any).
 */
export async function reconcileAndContinue(
  record: FtReceiverStateRecord,
  manifest: TransferManifest,
  deps: ReceiveResumeDeps,
  /**
   * True when `receiveHost` handed off after a P2P demotion, so the terminal
   * reports `relay_after_demote` rather than plain `relay`. Same bytes, opposite
   * signal about P2P health — see [FtTransport].
   */
  afterDemote = false
): Promise<void> {
  const transport: FtTransport = afterDemote ? "relay_after_demote" : "relay";
  // Built INSIDE the try (incrementally) so a mid-construction `openOutput` throw still lets the
  // `finally` dispose whatever handles were already created (a bare `.map()` before the try would
  // leak the earlier files' handles on such a throw). Multi-file-only concern; harmless otherwise.
  const outputs: ReceiveResumeOutput[] = [];
  try {
    for (let i = 0; i < manifest.files.length; i++) {
      outputs.push(deps.openOutput(record.transferId, i));
    }
    // Step 1 — per-file output check. Any file whose output is missing means
    // that file's already-downloaded bytes are gone — reset the WHOLE
    // record's GLOBAL cursor to 0 (mirrors the single-file "missing →
    // restart from zero" rule; a partial per-file reset would desync the
    // GLOBAL `chunksVerified` cursor from what's actually durable).
    const outputLengths: number[] = [];
    let anyMissing = false;
    for (const output of outputs) {
      const size = await output.probeSize();
      if (size === null) anyMissing = true;
      outputLengths.push(size ?? 0);
      await output.open(); // create-if-missing, never truncates
    }
    if (anyMissing) {
      record.chunksVerified = 0;
      record.partsDownloaded = [];
      record.updatedAt = nowOf(deps);
      await deps.saveReceiverState(record);
      for (let i = 0; i < outputLengths.length; i++) outputLengths[i] = 0;
    }

    // Step 2 — GET /v1/transfers/:id.
    let status: ResumeTransferStatus;
    try {
      status = await deps.api.getTransfer(record.transferId);
    } catch (err) {
      if (isDefinitive404(err)) {
        await createWaitOrExpire(record, manifest, outputs, outputLengths, deps, transport);
        return;
      }
      return; // NETWORK/other — never expire locally; leave "transferring"; stop
    }

    await continueFromGet(record, manifest, outputs, status, outputLengths, deps, transport);
  } finally {
    for (const output of outputs) output.dispose();
  }
}

/**
 * Explicit single-record resume — the entry point for a `"paused"` record's
 * peer-`resume`-ctrl transition back to `"transferring"` (§9.2). Runs the SAME
 * algorithm as crash recovery ("one resume code path").
 *
 * NOT dead code: the extension's `offscreen/index.ts` `handleInboundResumeCtrl`
 * (a peer-initiated `resume` wire ctrl — e.g. the phone's FGS 6h-park→Resume),
 * the web `receiveTransfer.web.ts` twin, and the CLI `receive` command all call
 * it, each feeding a freshly-read `FtReceiverStateRecord` through the identical
 * algorithm `resumePendingReceives`'s §6.1 crash-recovery scan uses ("one
 * resume code path"). The local user Resume click that also called it was
 * removed with the manual pause/resume cut. Do not delete.
 */
export async function resumeReceiverTransfer(
  record: FtReceiverStateRecord,
  deps: ReceiveResumeDeps
): Promise<void> {
  const manifest = validateRecord(record);
  if (manifest === null || isTerminalReceiverStatus(record.status)) return;
  // §9.2 PAUSED -> resume -> TRANSFERRING_RELAY: statuses map onto states
  // (§9 header), so a resumed record persists as "transferring" from the
  // algorithm's first natural persist onward (step 2's expiresAt save / a
  // restart-from-zero reset). In-memory flip only — a NETWORK failure before
  // any persist leaves the DISK record "paused" (§6.2 step 2's
  // never-expire-locally mirror of §5.2's "or 'paused' if that's how we got
  // here"), so a resume that never reached the server doesn't flip the
  // record into the auto-resume pool.
  if (record.status === "paused") record.status = "transferring";
  const gotLock = await deps.acquireLock(record.transferId);
  if (!gotLock) return;
  let began = false;
  try {
    began = deps.beginTransfer?.(record.transferId) ?? true;
    if (!began) return;
    await reconcileAndContinue(record, manifest, deps);
  } finally {
    if (began) deps.endTransfer?.(record.transferId);
    await deps.releaseLock(record.transferId);
  }
}

/**
 * §6.1 scan — the offscreen-host-startup + panel-init resume trigger. Loads
 * every receiver record, drops invalid ones (§2.4), skips terminal and
 * `"paused"` records (a crash while paused must never auto-resume), and
 * reconciles every `"transferring"` record under its single-writer lock.
 */
export async function resumePendingReceives(deps: ReceiveResumeDeps): Promise<void> {
  const rawRecords = await deps.listReceiverStates();

  for (const record of rawRecords) {
    if (deps.isAborted?.()) return;

    const manifest = validateRecord(record);
    if (manifest === null) {
      // §2.4 drop: delete the record AND its output artifact — but NEVER a
      // "complete" record's completed output (the user's file). The manifest
      // failed to parse/validate, so the file count is unknown; every record
      // predating multi-file (and every record this drop path can actually
      // reach in practice) is single-file, so cleaning up just fileIndex 0
      // covers the real cases without guessing at an unknowable count.
      if (record.status !== "complete") {
        const output = deps.openOutput(record.transferId, 0);
        try {
          await output.deleteCopy().catch(() => {});
        } finally {
          output.dispose();
        }
      }
      await deps.removeReceiverState(record.transferId);
      continue;
    }
    if (isTerminalReceiverStatus(record.status)) continue; // pruning is receiverState.ts's job (§8.5)
    if (record.status !== "transferring") continue; // "paused" — explicit resume only

    const gotLock = await deps.acquireLock(record.transferId);
    if (!gotLock) continue; // another context is already driving it

    let began = false;
    try {
      began = deps.beginTransfer?.(record.transferId) ?? true;
      if (!began) continue; // a fresh receive for this id is already running in this doc
      await reconcileAndContinue(record, manifest, deps);
    } finally {
      if (began) deps.endTransfer?.(record.transferId);
      await deps.releaseLock(record.transferId);
    }
  }
}

export type { ReceiveHandoffDisposition } from "./receiveLoop";
